import AccountSwitcher from '@ui/AccountSwitcher'
import BrandMark from '@ui/BrandMark'
import { IconRotateCcw } from '@ui/icons'
import {
  ACCOUNT_NAV,
  CATALOGUE_NAV_ITEM,
  EVENT_NAV,
  type NavItem,
  PLATFORM_NAV,
} from '@ui/nav-config'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'

import { useDevToolsEnabledQuery } from '@/__generated__/hooks'
import { useCurrentAccount } from '@/contexts/CurrentAccountProvider'

import { useEventMode } from './useEventMode'

type Props = {
  eventId: string | null
}

const SectionLabel = ({ children }: { children: string }) => (
  <p className="px-2.5 pt-4 pb-1.5 text-[11px] uppercase tracking-caps text-green-100/50 font-bold">
    {children}
  </p>
)

const ItemLink = ({ item }: { item: NavItem }) => {
  const { t } = useTranslation()
  return (
    <NavLink
      to={item.to}
      end={item.exact}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm font-medium transition-colors ${
          isActive
            ? 'bg-white text-green-900'
            : 'text-green-100/85 hover:bg-white/5 hover:text-white'
        }`
      }
    >
      <span className="grid place-items-center w-[18px] h-[18px]">
        <item.Icon size={16} />
      </span>
      {t(item.labelKey)}
    </NavLink>
  )
}

const DesktopSidebar = ({ eventId }: Props) => {
  const { t } = useTranslation()
  const currentAccount = useCurrentAccount()
  const isPlatform = currentAccount?.type === 'PLATFORM'
  const eventMode = useEventMode(eventId)
  // Only surfaces on environments with dev tools enabled (experimental /
  // staging-with-flag) — hidden on prod.
  const { data: devTools } = useDevToolsEnabledQuery({ skip: !isPlatform })
  const devToolsEnabled = devTools?.devToolsEnabled ?? false

  return (
    <aside className="bg-green-900 text-green-100 flex flex-col gap-3 sticky top-0 w-sidebar h-screen p-4">
      <div className="flex items-center gap-3 pb-3.5 border-b border-white/10">
        <BrandMark />
        <div className="leading-tight">
          <div className="font-display font-extrabold text-white text-xl tracking-tight">
            {t('brand.name')}
          </div>
          <div className="text-[11px] uppercase tracking-caps text-green-100/60">
            {t('brand.tagline')}
          </div>
        </div>
      </div>

      <AccountSwitcher variant="dark" />

      <nav className="flex flex-col gap-0.5 overflow-y-auto -mx-1 px-1">
        <SectionLabel>{t('nav.section.account')}</SectionLabel>
        {ACCOUNT_NAV.map((item) => (
          <ItemLink key={item.to} item={item} />
        ))}
        {!isPlatform && <ItemLink item={CATALOGUE_NAV_ITEM} />}

        {isPlatform && (
          <>
            <SectionLabel>{t('nav.section.platform')}</SectionLabel>
            {PLATFORM_NAV.map((item) => (
              <ItemLink key={item.to} item={item} />
            ))}
            {devToolsEnabled && (
              <ItemLink
                item={{
                  to: '/platform/dev-tools',
                  labelKey: 'nav.dev-tools',
                  shortLabelKey: 'nav.dev-tools',
                  Icon: IconRotateCcw,
                }}
              />
            )}
          </>
        )}

        {eventId && (
          <>
            <SectionLabel>{t('nav.section.event')}</SectionLabel>
            {EVENT_NAV(eventId, eventMode).map((item) => (
              <ItemLink key={item.to} item={item} />
            ))}
          </>
        )}
      </nav>

      <div className="mt-auto text-[11px] text-green-100/40 px-2.5 py-2">
        {t('version', { version: '0.0.0' })}
      </div>
    </aside>
  )
}

export default DesktopSidebar
